import { type ClassValue, clsx } from 'clsx';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Mask a phone number by replacing all but the last visible digits with '*'.
 * - Keeps a leading '+' if present.
 * - Only masks numeric digits; non-digit characters are removed in the result for simplicity.
 *
 * Examples:
 *  - "+84901234567" => "+*********567" (default lastVisibleDigits = 3)
 *  - "0901234567" => "*******567"
 */
export function maskPhone(
  phone: string,
  keepStartDigits: number = 0,
  keepEndDigits: number = 3,
  char = '*'
): string {
  if (!phone) return '';

  const totalDigits = (phone.match(/\d/g) || []).length;
  if (totalDigits === 0) return phone.replace(/./g, '*');

  const start = Math.max(0, Math.min(keepStartDigits, totalDigits));
  const end = Math.max(0, Math.min(keepEndDigits, totalDigits - start));

  let digitIndex = 0;
  let result = '';
  for (const ch of phone) {
    if (/\d/.test(ch)) {
      const isInStart = digitIndex < start;
      const isInEnd = digitIndex >= totalDigits - end;
      result += isInStart || isInEnd ? ch : char;
      digitIndex += 1;
    } else {
      // Keep non-digit characters unchanged to preserve original length/format
      result += ch;
    }
  }
  return result;
}

/**
 * Mask an email address by keeping the first and last character of the local part
 * and replacing the middle with '*'. The domain part remains unchanged.
 *
 * Examples:
 *  - "a@domain.com" => "a@domain.com" (no middle to mask)
 *  - "ab@domain.com" => "a*@domain.com"
 *  - "alice@example.com" => "a***e@example.com"
 */
export function maskEmail(
  email: string,

  keepStartChars: number = 1,
  keepEndChars: number = 1,
  char = '*'
): string {
  if (!email) return '';

  const atIndex = email.indexOf('@');
  if (atIndex === -1) {
    // Not a typical email shape; mask middle using generic logic
    const total = email.length;
    const start = Math.max(0, Math.min(keepStartChars, total));
    const end = Math.max(0, Math.min(keepEndChars, total - start));
    return email
      .split('')
      .map((ch, idx) => (idx < start || idx >= total - end ? ch : char))
      .join('');
  }

  // Gather indices of all characters except '@'
  const nonAtIndices: number[] = [];
  for (let i = 0; i < email.length; i += 1) {
    if (email[i] !== '@') nonAtIndices.push(i);
  }

  const totalNonAt = nonAtIndices.length;
  const startKeep = Math.max(0, Math.min(keepStartChars, totalNonAt));
  const endKeep = Math.max(0, Math.min(keepEndChars, totalNonAt - startKeep));

  const resultChars = email.split('');
  for (let k = 0; k < totalNonAt; k += 1) {
    const idx = nonAtIndices[k];
    const isInStart = k < startKeep;
    const isInEnd = k >= totalNonAt - endKeep;
    if (!isInStart && !isInEnd) {
      resultChars[idx] = char;
    }
  }

  return resultChars.join('');
}

export function formatDate(date: string | Date | dayjs.Dayjs) {
  return dayjs(date).format('DD/MM/YYYY');
}

export function formatDateTime(date: string | Date | dayjs.Dayjs) {
  return dayjs(date).format('DD/MM/YYYY HH:mm');
}

export function humanFileSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
  return `${value} ${sizes[i]}`;
}

export function decodeMojibake(text: string) {
  const bytes = Uint8Array.from(text.split('').map(char => char.charCodeAt(0)));

  return new TextDecoder('utf-8').decode(bytes);
}

export function encodeMojibake(text: string) {
  const bytes = new TextEncoder().encode(text);

  return Array.from(bytes)
    .map(byte => String.fromCharCode(byte))
    .join('');
}

/**
 * Convert large numbers to human-readable Vietnamese format
 *
 * @param value - The number to convert
 * @param group - Number of groups to show (1-3)
 * @returns Formatted string
 *
 * Examples:
 * - humanShorten(10000) => "10 nghìn"
 * - humanShorten(12345, 1) => "12,345 nghìn"
 * - humanShorten(12345678, 1) => "12,346 triệu"
 * - humanShorten(11234560000, 2) => "11 tỷ 234,56 triệu"
 * - humanShorten(11234560000, 3) => "11 tỷ 234 triệu 560 nghìn"
 */
export function humanShorten(value: number, group: number = 1): string {
  if (value === 0) return '0';

  const units = [
    { name: 'nghìn', value: 1000 },
    { name: 'triệu', value: 1000000 },
    { name: 'tỷ', value: 1000000000 },
    { name: 'nghìn tỷ', value: 1000000000000 },
  ];

  // Helper function to format number with comma separator
  const formatNumber = (num: number, decimals: number = 0): string => {
    if (decimals === 0) {
      return Math.round(num).toLocaleString('vi-VN');
    }
    const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    // Split into integer and decimal parts
    const parts = rounded.toString().split('.');
    const integerPart = parseInt(parts[0], 10).toLocaleString('vi-VN');
    if (parts.length > 1 && decimals > 0) {
      return `${integerPart},${parts[1].padEnd(decimals, '0').slice(0, decimals)}`;
    }
    return integerPart;
  };

  // Find the appropriate unit
  let unitIndex = 0;
  for (let i = units.length - 1; i >= 0; i--) {
    if (value >= units[i].value) {
      unitIndex = i;
      break;
    }
  }

  const result: string[] = [];
  let remaining = value;

  // Process groups from largest to smallest
  for (let i = unitIndex; i >= 0 && result.length < group; i--) {
    const unit = units[i];
    const isLastGroup = result.length === group - 1;
    const count = remaining / unit.value;

    if (count >= 1 || (!isLastGroup && count > 0)) {
      let formattedCount: string;

      if (group === 1) {
        // For single group, round to 3 decimal places if needed, then format with comma
        // Check if rounding is needed (if has more than 3 significant decimal digits)
        const decimalPart = count - Math.floor(count);
        if (decimalPart === 0) {
          formattedCount = formatNumber(count, 0);
        } else {
          // Round to 3 decimal places if needed
          const rounded = Math.round(count * 1000) / 1000;
          const roundedDecimal = rounded - Math.floor(rounded);
          if (roundedDecimal === 0) {
            formattedCount = formatNumber(Math.round(rounded), 0);
          } else {
            // Check how many decimal places we need
            const str = rounded.toString();
            const dotIndex = str.indexOf('.');
            if (dotIndex !== -1) {
              const decimals = str.length - dotIndex - 1;
              formattedCount = formatNumber(rounded, Math.min(decimals, 3));
            } else {
              formattedCount = formatNumber(rounded, 0);
            }
          }
        }
      } else if (isLastGroup && group > 1) {
        // For last group when multiple groups, show up to 2 decimals
        const remainder = count - Math.floor(count);
        if (remainder === 0) {
          formattedCount = formatNumber(count, 0);
        } else {
          // Round to 2 decimal places
          formattedCount = formatNumber(count, 2);
        }
      } else {
        // For non-last groups, show integer with comma
        formattedCount = formatNumber(Math.floor(count), 0);
      }

      result.push(`${formattedCount} ${unit.name}`);
      remaining = remaining % unit.value;
    }
  }

  return result.join(' ');
}

/**
 * Convert a date to human-readable "time ago" format in Vietnamese
 *
 * @param date - The date to convert (string, Date, or dayjs.Dayjs)
 * @returns Formatted string like "2 giờ trước", "hôm nay", "hôm qua"
 *
 * Examples:
 * - timeAgo(new Date()) => "vừa xong"
 * - timeAgo(dayjs().subtract(2, 'hour')) => "2 giờ trước"
 * - timeAgo(dayjs().startOf('day')) => "hôm nay"
 * - timeAgo(dayjs().subtract(1, 'day')) => "hôm qua"
 * - timeAgo(dayjs().subtract(3, 'day')) => "3 ngày trước"
 * - timeAgo(dayjs().subtract(1, 'month')) => "1 tháng trước"
 */
export function timeAgo(date: string | Date | dayjs.Dayjs | number): string {
  if (typeof date === 'number') {
    date = new Date(date / 1000);
  }

  const now = dayjs();
  const targetDate = dayjs(date);
  const diff = now.diff(targetDate);

  // If the date is in the future, return "sắp tới"
  if (diff < 0) {
    return 'sắp tới';
  }

  // If less than 1 minute
  if (diff < 60000) {
    return 'vừa xong';
  }

  // If less than 1 hour
  if (now.diff(targetDate, 'hour') < 1) {
    return `${now.diff(targetDate, 'minute')} phút trước`;
  }

  // Check if it's today (same day)
  if (now.isSame(targetDate, 'day')) {
    return 'hôm nay';
  }

  // Check if it's yesterday
  if (now.subtract(1, 'day').isSame(targetDate, 'day')) {
    return 'hôm qua';
  }

  // If less than 1 month
  if (now.diff(targetDate, 'month') < 1) {
    return `${now.diff(targetDate, 'day')} ngày trước`;
  }

  // If less than 1 year
  if (now.diff(targetDate, 'year') < 1) {
    return `${now.diff(targetDate, 'month')} tháng trước`;
  }

  // More than 1 year
  return `${now.diff(targetDate, 'year')} năm trước`;
}

export const arrayTrail = (array: any[], size: number) => {
  if (array.length < size)
    return [[...array, ...Array.from({ length: size - array.length }, () => null)]];

  const parts = array.splice(0, size);

  return [parts, array];
};

/**
 * Parse number from various formats including Vietnamese number formats
 * Handles formats like "300,00", "300.00", "300,000", "300.000", etc.
 *
 * @param value - The string value to parse
 * @returns Parsed number or NaN if invalid
 *
 * Examples:
 * - parseNumber("300,00") => 300
 * - parseNumber("300.00") => 300
 * - parseNumber("300,000") => 300000
 * - parseNumber("300.000") => 300000
 * - parseNumber("1.234.567,89") => 1234567.89
 * - parseNumber("1,234,567.89") => 1234567.89
 */
export function parseNumber(value: string | number | undefined): number | string {
  if (typeof value === 'number' || !value) return (value as string) || '';
  if (!value || typeof value !== 'string') return '';

  // Remove all spaces
  let cleanValue = value.trim().replace(/\s/g, '');

  if (!cleanValue) return '';

  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  if (isNegative) {
    cleanValue = cleanValue.substring(1);
  }

  // Check if it's a valid number format
  // Allow: digits, one decimal separator, and thousands separators
  const validPattern = /^[\d.,]+$/;
  if (!validPattern.test(cleanValue)) return '';

  // Count decimal separators and thousands separators
  const commaCount = (cleanValue.match(/,/g) || []).length;
  const dotCount = (cleanValue.match(/\./g) || []).length;

  // Determine format based on separator positions
  let result: number | string;

  if (commaCount === 0 && dotCount === 0) {
    // No separators, just digits
    result = parseInt(cleanValue, 10);
  } else if (commaCount === 1 && dotCount === 0) {
    // Format: "123,45" (European decimal)
    const parts = cleanValue.split(',');
    if (parts.length === 2) {
      result = parseFloat(parts[0] + '.' + parts[1]);
    } else {
      result = parseInt(cleanValue.replace(/,/g, ''), 10);
    }
  } else if (commaCount === 0 && dotCount === 1) {
    // Format: "123.45" (US decimal) or "123.000" (thousands)
    const parts = cleanValue.split('.');
    if (parts.length === 2) {
      // Check if it's decimal (2 digits after dot) or thousands (3 digits after dot)
      if (parts[1].length <= 2) {
        // Likely decimal
        result = parseFloat(cleanValue);
      } else {
        // Likely thousands separator
        result = parseInt(cleanValue.replace(/\./g, ''), 10);
      }
    } else {
      result = parseInt(cleanValue, 10);
    }
  } else if (commaCount > 0 && dotCount === 1) {
    // Format: "1,234.56" (US thousands + decimal)
    result = parseFloat(cleanValue.replace(/,/g, ''));
  } else if (commaCount === 1 && dotCount > 0) {
    // Format: "1.234,56" (European thousands + decimal)
    const parts = cleanValue.split(',');
    if (parts.length === 2) {
      const integerPart = parts[0].replace(/\./g, '');
      result = parseFloat(integerPart + '.' + parts[1]);
    } else {
      result = parseInt(cleanValue.replace(/[.,]/g, ''), 10);
    }
  } else {
    // Multiple separators of same type - treat as thousands separators
    if (commaCount > 1) {
      result = parseInt(cleanValue.replace(/,/g, ''), 10);
    } else if (dotCount > 1) {
      result = parseInt(cleanValue.replace(/\./g, ''), 10);
    } else {
      result = '';
    }
  }

  return isNegative ? -result : (result as number);
}

export const dataURLToBlob = (dataURL: string) => {
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

export function validateEmail(email?: string): boolean {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateCitizenNumber(citizenNumber?: string): boolean {
  if (!citizenNumber) return false;
  const citizenNumberRegex = /^[0-9]{12}$/;
  return citizenNumberRegex.test(citizenNumber);
}

/**
 * Extract năm sinh từ CCCD
 * Format CCCD: AAABCCXXXXXX (12 chữ số)
 * - B (chữ số thứ 4): xác định thế kỷ (0,1 = thế kỷ 20; 2,3 = thế kỷ 21; 4,5 = thế kỷ 22)
 * - CC (chữ số 5-6): 2 chữ số cuối của năm sinh
 */
export function extractBirthYearFromCCCD(citizenNumber?: string): number | null {
  if (!citizenNumber || citizenNumber.length !== 12) return null;

  const B = parseInt(citizenNumber.charAt(3), 10);
  const CC = citizenNumber.substring(4, 6);

  let century: number;
  if (B === 0 || B === 1) {
    century = 1900;
  } else if (B === 2 || B === 3) {
    century = 2000;
  } else if (B === 4 || B === 5) {
    century = 2100;
  } else {
    return null;
  }

  return century + parseInt(CC, 10);
}

/**
 * Validate năm sinh với CCCD
 * Trả về true nếu năm sinh khớp với CCCD
 */
export function validateBirthYearWithCCCD(
  birthYear?: string | number,
  citizenNumber?: string
): boolean {
  if (!birthYear || !citizenNumber) return true; // Bỏ qua nếu chưa có đủ thông tin

  const birthYearNum = typeof birthYear === 'string' ? parseInt(birthYear, 10) : birthYear;
  const cccdBirthYear = extractBirthYearFromCCCD(citizenNumber);

  if (!cccdBirthYear || isNaN(birthYearNum)) return true;

  return birthYearNum === cccdBirthYear;
}

/**
 * Validate tuổi >= 18
 */
export function validateAge18(birthYear?: string | number): boolean {
  if (!birthYear) return true; // Bỏ qua nếu chưa có

  const birthYearNum = typeof birthYear === 'string' ? parseInt(birthYear, 10) : birthYear;
  if (isNaN(birthYearNum)) return true;

  const currentYear = new Date().getFullYear();
  return currentYear - birthYearNum >= 18;
}

export function validatePhone(phone?: string): boolean {
  if (!phone) return false;
  const phoneRegex = /^[0-9]{10,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
}

export function validateName(name?: string): boolean {
  if (!name) return false;
  const nameRegex = /^[a-zA-ZÀ-ỹ\s]+$/;
  return nameRegex.test(name);
}

export function clamp(
  value: number,
  [min, max]: [number?, number?] = [undefined, undefined]
): number {
  min = min ?? value;
  max = max ?? value;

  return Math.max(min, Math.min(value, max));
}

export function clampToString(value: number, min: number, max: number): string {
  value = clamp(value, [min]);

  if (value < max) return value.toString();

  return `${max}+`;
}

Object.defineProperties(Object.prototype, {
  fetch: {
    enumerable: false,
    writable: true,
    value(this: any, ...keys: string[]) {
      if (!this) return null;

      return keys.reduce((value, key) => {
        if (!value) return value;
        if (Array.isArray(key)) return value.fetchMulti(...key);
        if (!/\./.test(key)) return value[key];

        return value.fetch(...key.split('.'));
      }, this);
    },
  },

  fetchMulti: {
    enumerable: false,
    writable: true,
    value(this: any, ...keys: any[]) {
      if (!this) return null;

      return keys.reduce((value: any, key: string | string[]) => {
        let keyz: any;
        if (Array.isArray(key)) keyz = key.join('.');
        else keyz = key;

        keyz = keyz.split(':');
        let [, castName] = keyz;
        const [fetchKey] = keyz;
        if (!castName) [castName] = fetchKey.split('.').slice(-1);

        value[castName] = this.fetch(...fetchKey.split('.'));

        return value;
      }, {});
    },
  },
});

export {};
